import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, X, RotateCcw } from "lucide-react";
import { Mark } from "./Wordmark";
import { AssistantMarkdown } from "./AssistantMarkdown";
import { cn } from "@/lib/utils";

const API = "/.netlify/functions/assistant";
const MODEL = "Selling Sunsets AI";
const STORAGE_KEY = "ss_assistant_messages";

const STARTERS = [
  "What should I verify before buying land near Mukteshwar?",
  "How do agricultural land rules in Uttarakhand affect an outside buyer?",
  "What does a sensible due-diligence sequence look like for a hill property?",
];

export const AskAssistant = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try { setMessages(JSON.parse(saved)); } catch (_) {}
    }
  }, []);

  useEffect(() => {
    if (messages.length) localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 350);
  }, [open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;

    const history = messages
      .filter((m) => !m.streaming && !m.error)
      .map(({ role, content }) => ({ role, content }));

    setInput("");
    setBusy(true);
    setMessages((m) => [
      ...m,
      { role: "user", content: message, model: MODEL },
      { role: "assistant", content: "", model: MODEL, streaming: true },
    ]);

    try {
      const res = await fetch(API, {
        method: "POST",
        signal: AbortSignal.timeout(15000),
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: [...history, { role: "user", content: message }] }),
      });

      const raw = await res.text();
      let data;
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch (_) {
        throw new Error("The assistant is temporarily unavailable. Please try again in a moment.");
      }
      if (!res.ok) throw new Error(data.error || "The assistant could not complete your request. Please try again.");
      if (!data.text) throw new Error("The assistant returned an empty response. Please try again.");

      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: "assistant", content: data.text, model: MODEL, streaming: false, mode: data.mode, sources: data.sources || [] };
        return next;
      });
    } catch (e) {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: "assistant",
          content: "I cannot reach the research service right now. You can browse our location guides or discuss your question with Selling Sunsets.",
          sources: [{title: "Location guides", path: "/locations"}, {title: "Discuss your question", path: "/advisory"}],
          model: MODEL,
          error: true,
          streaming: false,
        };
        return next;
      });
    } finally {
      setBusy(false);
    }
  };

  const newConversation = () => {
    localStorage.removeItem(STORAGE_KEY);
    setMessages([]);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Ask Selling Sunsets"
        data-testid="assistant-launcher"
        className={cn(
          "fixed bottom-6 right-6 z-40 inline-flex items-center gap-3 pl-3 pr-4 py-2.5 bg-[#1A1C1E] text-[#F7F5F0] shadow-[0_8px_30px_rgba(0,0,0,0.18)] transition-all duration-500",
          open ? "opacity-0 pointer-events-none translate-y-2" : "opacity-100 hover:bg-[#2D3B32]"
        )}
      >
        <Mark variant="symbol" className="h-[16px]" />
        <span className="font-mono-ss text-[0.64rem] tracking-[0.2em] uppercase">Ask</span>
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div className="fixed inset-0 z-40 bg-[#181A1B]/30 backdrop-blur-[2px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} />
            <motion.aside role="dialog" aria-label="Ask Selling Sunsets" data-testid="assistant-panel" className="fixed inset-y-0 right-0 z-50 w-full sm:w-[440px] bg-[#F7F5F0] border-l border-[#E2DDD5] flex flex-col" initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}>
              <header className="px-6 pt-6 pb-4 border-b border-[#E2DDD5] flex items-start justify-between gap-4">
                <div>
                  <p className="ss-overline mb-2">Research assistant</p>
                  <p className="font-display text-2xl font-light leading-none text-[#1A1C1E]">Ask Selling Sunsets</p>
                </div>
                <div className="flex items-center gap-1">
                  {messages.length > 0 && <button onClick={newConversation} className="p-2 text-[#8A9098] hover:text-[#1A1C1E] transition-colors" aria-label="New conversation"><RotateCcw className="h-4 w-4" strokeWidth={1.5} /></button>}
                  <button onClick={() => setOpen(false)} className="p-2 -mr-2 text-[#1A1C1E]" aria-label="Close"><X className="h-5 w-5" strokeWidth={1.5} /></button>
                </div>
              </header>

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-6" data-testid="assistant-messages">
                {messages.length === 0 && (
                  <div className="space-y-6">
                    <p className="text-[0.95rem] leading-[1.75] font-light text-[#3a3f44]">Ask about locations, land, law and due diligence across Uttarakhand. Answers draw on Selling Sunsets research and point you to free, official sources to verify.</p>
                    <div className="space-y-px">
                      {STARTERS.map((s) => <button key={s} onClick={() => send(s)} className="w-full text-left px-4 py-3.5 bg-[#EFECE6] hover:bg-[#E6E2DA] transition-colors text-[0.86rem] leading-snug text-[#1A1C1E]">{s}</button>)}
                    </div>
                  </div>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                    {m.role === "user" ? (
                      <p className="max-w-[85%] bg-[#1A1C1E] text-[#F7F5F0] px-4 py-3 text-[0.9rem] leading-relaxed">{m.content}</p>
                    ) : (
                      <div className="max-w-[94%]">
                        <p className="ss-overline !text-[0.58rem] mb-2">{m.mode === "guides" ? "Selling Sunsets · Published research" : "Selling Sunsets · Property Intelligence"}</p>
                        <div className={cn("text-[0.9rem] leading-[1.75] font-light text-[#2b2f33]", m.error && "text-[#8c3b3b]")}>
                          {m.content ? <AssistantMarkdown text={m.content} /> : <span className="inline-block h-4 w-[2px] bg-[#1A1C1E] animate-pulse" />}
                          {Array.isArray(m.sources) && m.sources.filter(s => typeof s.path === "string" && s.path.startsWith("/") && !s.path.startsWith("//")).map(s => <a key={s.path} href={s.path} className="block mt-3 underline underline-offset-4">{s.title}</a>)}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-[#E2DDD5] px-6 pt-4 pb-5 space-y-3">
                <div className="flex items-end gap-3 border border-[#C8C2B7] focus-within:border-[#1A1C1E] transition-colors bg-transparent">
                  <textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={2} placeholder="Ask about a place, a plot, a rule…" className="flex-1 resize-none bg-transparent px-4 py-3 text-[0.9rem] leading-relaxed text-[#1A1C1E] placeholder:text-[#8A9098] outline-none" />
                  <button type="submit" disabled={busy || !input.trim()} aria-label="Send" className="m-2 h-9 w-9 inline-flex items-center justify-center bg-[#1A1C1E] text-[#F7F5F0] disabled:opacity-30 transition-opacity"><ArrowUp className="h-4 w-4" strokeWidth={1.5} /></button>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <p className="font-mono-ss text-[0.6rem] tracking-[0.16em] uppercase text-[#8A9098]">Property intelligence assistant</p>
                  <p className="font-mono-ss text-[0.58rem] tracking-[0.12em] uppercase text-[#8A9098] text-right">Research guidance · not legal advice</p>
                </div>
              </form>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default AskAssistant;

